import React, { useState } from 'react';
import { 
  FOUNDER_CONFIG, 
  CONFERENCE_CONFIG, 
  PRODUCT_VALUE_PILLARS,
  ROLE_SEGMENT_OPTIONS 
} from '../config/conference';
import { BadgeScanData } from '../utils/badgeTracker';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { PageView } from '../types';
import { 
  Linkedin, 
  Calendar, 
  Mail, 
  CheckCircle2, 
  ArrowUpRight, 
  Clock, 
  Sparkles, 
  ArrowRight,
  Download,
  MapPin,
  ChevronDown
} from 'lucide-react';

interface BadgeHeroCardProps {
  badgeData: BadgeScanData;
  onOpenLeadModal: () => void;
  setActivePage: (page: PageView) => void;
}

export const BadgeHeroCard: React.FC<BadgeHeroCardProps> = ({
  badgeData,
  onOpenLeadModal,
  setActivePage
}) => {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mailToUrl, setMailToUrl] = useState('');

  const handleSendDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;

    setSubmitting(true);

    const leadRecord = {
      email,
      jobRole: selectedRole || 'Not specified (Post-Conversation)',
      roleSegment: selectedRole || null,
      badgeTimestamp: badgeData.timestampMs || null,
      formattedBadgeTime: badgeData.formattedTime || null,
      rawParam: badgeData.rawParam || null,
      conference: badgeData.conference || CONFERENCE_CONFIG.defaultName,
      source: 'Post-Conversation Badge Page',
      clientCreatedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      createdAt: serverTimestamp()
    };

    // Save to Firestore (both demo_requests and badge_leads for redundancy)
    try {
      await addDoc(collection(db, 'demo_requests'), {
        fullName: 'Warm Conference Contact',
        email,
        company: 'Conference Lead',
        jobRole: selectedRole || 'Post-Conversation Follow-Up',
        networkSize: 'Not specified',
        comments: `Met at ${CONFERENCE_CONFIG.defaultName} in ${CONFERENCE_CONFIG.location}. Requested 1-pager & pricing guide.${selectedRole ? ` Role/Focus: ${selectedRole}.` : ''} Badge timestamp: ${badgeData.formattedTime || badgeData.rawParam || 'None'}`,
        badgeTimestamp: badgeData.timestampMs || null,
        formattedBadgeTime: badgeData.formattedTime || null,
        conference: badgeData.conference || CONFERENCE_CONFIG.defaultName,
        source: 'Post-Conversation Badge Page',
        createdAt: serverTimestamp()
      });
      await addDoc(collection(db, 'badge_leads'), leadRecord);
    } catch (err) {
      console.warn('[BadgeHeroCard] Error saving badge lead to Firestore:', err);
    }

    // Optional webhook trigger
    const webhookUrl = (import.meta as any).env?.VITE_LEAD_WEBHOOK_URL;
    if (webhookUrl) {
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadRecord)
        });
      } catch (webhookErr) {
        console.warn('[BadgeHeroCard] Webhook error:', webhookErr);
      }
    }

    // Generate prefilled mailto link
    const roleSnippet = selectedRole ? ` (${selectedRole})` : '';
    const subject = encodeURIComponent(`MUSE Telemetry Follow-Up - ${CONFERENCE_CONFIG.defaultName}`);
    const body = encodeURIComponent(
      `Hi BJ,\n\nGreat meeting you at ${CONFERENCE_CONFIG.defaultName} in ${CONFERENCE_CONFIG.location}!\n\n` +
      `Here is my email (${email})${roleSnippet} to stay connected. Please send over the architecture breakdown and pricing guide we discussed.\n\n` +
      `Looking forward to continuing the conversation.\n\n` +
      `Best,\n`
    );
    const generatedMailto = `mailto:${encodeURIComponent(FOUNDER_CONFIG.email)}?subject=${subject}&body=${body}`;
    setMailToUrl(generatedMailto);

    setSubmitted(true);
    setSubmitting(false);
  };

  const handleDownloadSummary = () => {
    const content = 
`=============================================================
MUSE TELEMETRY: POST-CONVERSATION REFERENCE & ONE-PAGER
The Vendor-Independent Intelligence Layer for Rural Broadband
=============================================================

Event: ${CONFERENCE_CONFIG.defaultName}
Location: ${CONFERENCE_CONFIG.venue} | ${CONFERENCE_CONFIG.location}
Founder: ${FOUNDER_CONFIG.name} (${FOUNDER_CONFIG.title})
Badge Scanned: ${badgeData.formattedTime || new Date().toLocaleTimeString()}
${selectedRole ? `Attendee Focus / Role: ${selectedRole}\n` : ''}
KEY TAKEAWAYS FROM OUR CONVERSATION:
1. ONE PANE OF GLASS (0 VENDOR LOCK-IN)
   Unified DOCSIS, PON, GPON, Arista, and Cisco console. Buy gear on 
   technical merit and price rather than closed software silos.

2. PREDICTABLE PER-SUBSCRIBER PRICING
   Per-subscriber model that scales directly with your revenue. 
   Built for independent rural telcos and fiber operators.

3. TOPOLOGICAL ROOT-CAUSE CLUSTERING
   Correlates fiber paths to isolate upstream failures instantly. 
   Suppresses 200+ cascading alarms to eliminate wasted truck rolls.

STAY CONNECTED & SCHEDULE DEEPER REVIEW:
• LinkedIn: ${FOUNDER_CONFIG.linkedinUrl}
• 15-Min Operational Review: ${FOUNDER_CONFIG.bookingUrl}
• Direct Email: ${FOUNDER_CONFIG.email}
• Interactive Sandbox: https://demo.musetelemetry.com
=============================================================`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `MUSE-Telemetry-FollowUp-${CONFERENCE_CONFIG.location.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const directEmailHref = `mailto:${FOUNDER_CONFIG.email}?subject=${encodeURIComponent(`Great meeting you at ${CONFERENCE_CONFIG.defaultName}`)}`;

  const scrollToFullSite = () => {
    const el = document.getElementById('full-site-content');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' });
    }
  };

  return (
    <section className="engagement-page-section">
      <div className="section-container">
        <div className="engagement-card">
          
          {/* 1. Header Verification Bar */}
          <div className="card-top-bar">
            <div className="badge-connected-tag">
              <span className="live-dot-pulse" />
              <span className="live-tag-text font-mono">
                {badgeData.isBadgeScan ? 'E-PAPER BADGE CONNECTED' : 'CONFERENCE CONNECT'}
              </span>
            </div>

            <div className="badge-location-meta font-mono">
              <span className="location-chip">
                <MapPin size={11} />
                <span>{CONFERENCE_CONFIG.location}</span>
              </span>
              {badgeData.formattedTime && (
                <span className="time-chip">
                  <Clock size={11} />
                  <span>{badgeData.formattedTime}</span>
                </span>
              )}
            </div>
          </div>

          {/* 2. Hero & Warm Post-Conversation Callback */}
          <div className="engagement-hero">
            <div className="founder-mini-profile">
              <div className="founder-avatar-circle">
                <span className="avatar-initials">BM</span>
              </div>
              <div className="founder-meta">
                <div className="founder-name-wrap">
                  <span className="founder-name">{FOUNDER_CONFIG.name}</span>
                  <span className="founder-title-badge font-mono">FOUNDER</span>
                </div>
                <span className="founder-sub">{FOUNDER_CONFIG.credentials}</span>
              </div>
            </div>

            <h1 className="engagement-headline">
              Great Meeting You at {CONFERENCE_CONFIG.defaultName}!
            </h1>

            <p className="engagement-subheadline">
              Whether we talked about slashing opex for fiber operators, untangling vendor chaos, or building visibility that actually scales,  let's keep the conversation going.
            </p>

            <p className="engagement-callback-note">
              Thanks for stopping by! Let's continue what we started, whether you want to connect on LinkedIn, grab 15 minutes to go deeper, or just shoot me a quick email.
            </p>
          </div>

          {/* 3. Primary CTAs (The Centerpiece of the Page) */}
          <div className="primary-cta-grid">
            
            {/* CTA 1: LinkedIn */}
            <a
              href={FOUNDER_CONFIG.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-card cta-linkedin"
            >
              <div className="cta-icon-wrap icon-linkedin">
                <Linkedin size={22} />
              </div>
              <div className="cta-text-wrap">
                <div className="cta-title-row">
                  <span className="cta-title">Connect on LinkedIn</span>
                  <ArrowUpRight size={14} className="cta-arrow" />
                </div>
                <span className="cta-subtitle">Stay in my network</span>
              </div>
            </a>

            {/* CTA 2: Schedule Call */}
            <a
              href={FOUNDER_CONFIG.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-card cta-calendar"
            >
              <div className="cta-icon-wrap icon-calendar">
                <Calendar size={22} />
              </div>
              <div className="cta-text-wrap">
                <div className="cta-title-row">
                  <span className="cta-title">Schedule 15-Min Call</span>
                  <ArrowUpRight size={14} className="cta-arrow" />
                </div>
                <span className="cta-subtitle">Let's dive deeper</span>
              </div>
            </a>

            {/* CTA 3: Email */}
            <a
              href={directEmailHref}
              className="cta-card cta-email"
            >
              <div className="cta-icon-wrap icon-email">
                <Mail size={22} />
              </div>
              <div className="cta-text-wrap">
                <div className="cta-title-row">
                  <span className="cta-title">Send Me an Email</span>
                  <ArrowUpRight size={14} className="cta-arrow" />
                </div>
                <span className="cta-subtitle">Quick question? Reach out</span>
              </div>
            </a>

          </div>

          {/* 4. Single-Field Email + Optional Role Dropdown Capture Form */}
          <div className="simplified-email-box">
            <div className="email-box-header">
              <Sparkles size={16} className="text-blue" />
              <h2 className="email-box-title">Want the one-pager? Drop your email.</h2>
            </div>
            <p className="email-box-subtitle">
              No spam. Just the architecture breakdown and pricing guide we discussed.
            </p>

            {!submitted ? (
              <form onSubmit={handleSendDetails} className="simplified-form">
                <div className="form-inline-row">
                  <div className="input-field-wrap input-email-wrap">
                    <input
                      type="email"
                      required
                      placeholder="you@regionalfiber.net"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="single-work-input"
                      autoComplete="email"
                      inputMode="email"
                      id="badge-lead-email"
                    />
                  </div>

                  <div className="input-field-wrap input-role-wrap">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className={`single-role-select ${selectedRole ? 'has-value' : ''}`}
                      id="badge-lead-role"
                      aria-label="Role or focus (optional)"
                    >
                      <option value="">Role / Focus (Optional)</option>
                      {ROLE_SEGMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="role-select-chevron" />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-submit-single"
                    id="badge-lead-submit"
                  >
                    <span>{submitting ? 'Sending...' : 'Send Me the Details'}</span>
                    <ArrowRight size={15} />
                  </button>
                </div>
              </form>
            ) : (
              <div className="form-success-wrapper">
                <div className="success-banner">
                  <CheckCircle2 size={20} className="text-green" />
                  <div className="success-text-col">
                    <span className="success-main-text">
                      Details registered for <strong>{email}</strong>
                    </span>
                    {selectedRole && (
                      <span className="success-role-tag font-mono">
                        {selectedRole}
                      </span>
                    )}
                  </div>
                </div>

                <div className="instant-action-row">
                  {mailToUrl && (
                    <a href={mailToUrl} className="btn-instant-small btn-mail-instant">
                      <Mail size={14} />
                      <span>Open Pre-Filled Email</span>
                    </a>
                  )}
                  <button onClick={handleDownloadSummary} className="btn-instant-small btn-dl-instant">
                    <Download size={14} />
                    <span>Download 1-Pager Text File</span>
                  </button>
                </div>

                <button 
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                    setSelectedRole('');
                  }} 
                  className="btn-reenter-email"
                >
                  Enter another email
                </button>
              </div>
            )}
          </div>

          {/* 5. Key Differentiators / Quick Reference (Condensed 3 Cards) */}
          <div className="quick-reference-section">
            <span className="reference-heading font-mono">QUICK REFERENCE: WHAT WE DISCUSSED</span>
            <div className="reference-cards-grid">
              {PRODUCT_VALUE_PILLARS.map((pillar, idx) => (
                <div key={idx} className="reference-card">
                  <div className="ref-card-head">
                    <span className="ref-title">{pillar.title}</span>
                    <span className="ref-metric font-mono">{pillar.metric}</span>
                  </div>
                  <p className="ref-desc">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 6. Subtle Footnote for Deeper Exploration */}
          <div className="footnote-deep-explore">
            <span className="footnote-text">Looking for full technical docs or live visualizers?</span>
            <div className="footnote-actions">
              <button 
                onClick={() => { setActivePage('forge'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
                className="footnote-link-btn font-mono"
              >
                MUSE Forge Sandbox
              </button>
              <button 
                onClick={onOpenLeadModal} 
                className="footnote-link-btn font-mono"
              >
                Detailed 15-Min Review Form
              </button>
              <button onClick={scrollToFullSite} className="footnote-explore-btn font-mono">
                <span>View Full Site Below</span>
                <ChevronDown size={13} />
              </button>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .engagement-page-section {
          padding: 1.5rem 0 2.5rem;
          position: relative;
          z-index: 10;
        }

        .engagement-card {
          background: #05080e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-lg);
          padding: 2rem;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.8), 0 0 40px rgba(37, 99, 235, 0.09);
          position: relative;
          overflow: hidden;
          max-width: 920px;
          margin: 0 auto;
        }

        /* Subtle ambient glow top right */
        .engagement-card::before {
          content: '';
          position: absolute;
          top: -100px;
          right: -100px;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(37, 99, 235, 0.22) 0%, transparent 70%);
          pointer-events: none;
        }

        /* 1. Header Bar */
        .card-top-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          padding-bottom: 1.25rem;
          margin-bottom: 1.75rem;
          border-bottom: 1px solid #1e293b;
        }

        .badge-connected-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          background: rgba(37, 99, 235, 0.12);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 999px;
          padding: 0.35rem 0.85rem;
        }

        .live-dot-pulse {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          box-shadow: 0 0 8px #10b981;
          animation: pulseDot 2s infinite ease-in-out;
        }

        @keyframes pulseDot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.6; }
        }

        .live-tag-text {
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          color: #93c5fd;
        }

        .badge-location-meta {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.75rem;
        }

        .location-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.25);
          padding: 0.25rem 0.65rem;
          border-radius: 4px;
          color: #fbbf24;
          font-weight: 500;
        }

        .time-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: #090d14;
          border: 1px solid #1e293b;
          padding: 0.25rem 0.65rem;
          border-radius: 4px;
          color: #38bdf8;
        }

        /* 2. Hero Section */
        .engagement-hero {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .founder-mini-profile {
          display: flex;
          align-items: center;
          gap: 0.85rem;
        }

        .founder-avatar-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1e3a8a, #2563eb);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #3b82f6;
          box-shadow: 0 0 16px rgba(59, 130, 246, 0.35);
          flex-shrink: 0;
        }

        .avatar-initials {
          font-weight: 800;
          font-size: 1.1rem;
          color: #ffffff;
        }

        .founder-meta {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .founder-name-wrap {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .founder-name {
          font-size: 1.15rem;
          font-weight: 700;
          color: #ffffff;
        }

        .founder-title-badge {
          background: rgba(37, 99, 235, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          color: #60a5fa;
          font-size: 0.62rem;
          font-weight: 700;
          padding: 0.1rem 0.45rem;
          border-radius: 4px;
          letter-spacing: 0.05em;
        }

        .founder-sub {
          font-size: 0.78rem;
          color: #94a3b8;
        }

        .engagement-headline {
          font-size: 1.85rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.25;
          letter-spacing: -0.02em;
          margin: 0;
        }

        .engagement-subheadline {
          font-size: 1.05rem;
          color: #cbd5e1;
          line-height: 1.5;
          margin: 0;
        }

        .engagement-callback-note {
          font-size: 0.92rem;
          color: #94a3b8;
          line-height: 1.5;
          background: rgba(15, 23, 42, 0.65);
          border-left: 3px solid #3b82f6;
          padding: 0.85rem 1.1rem;
          border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
          margin: 0.25rem 0 0 0;
        }

        /* 3. Primary CTAs (3 Equal-Weight Cards) */
        .primary-cta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .cta-card {
          display: flex;
          align-items: center;
          gap: 0.85rem;
          padding: 1.15rem 1rem;
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid #1e293b;
          background: #090d14;
          box-sizing: border-box;
          min-height: 80px;
        }

        .cta-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
        }

        .cta-linkedin {
          border-color: rgba(10, 102, 194, 0.4);
          background: linear-gradient(180deg, rgba(10, 102, 194, 0.12) 0%, #090d14 100%);
        }
        .cta-linkedin:hover {
          border-color: #0a66c2;
          box-shadow: 0 8px 24px rgba(10, 102, 194, 0.25);
        }

        .cta-calendar {
          border-color: rgba(37, 99, 235, 0.4);
          background: linear-gradient(180deg, rgba(37, 99, 235, 0.14) 0%, #090d14 100%);
        }
        .cta-calendar:hover {
          border-color: #3b82f6;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.3);
        }

        .cta-email {
          border-color: rgba(16, 185, 129, 0.4);
          background: linear-gradient(180deg, rgba(16, 185, 129, 0.12) 0%, #090d14 100%);
        }
        .cta-email:hover {
          border-color: #10b981;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25);
        }

        .cta-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .icon-linkedin { background: rgba(10, 102, 194, 0.2); color: #38bdf8; }
        .icon-calendar { background: rgba(37, 99, 235, 0.2); color: #60a5fa; }
        .icon-email { background: rgba(16, 185, 129, 0.2); color: #34d399; }

        .cta-text-wrap {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
          min-width: 0;
        }

        .cta-title-row {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }

        .cta-title {
          font-size: 0.95rem;
          font-weight: 700;
          color: #ffffff;
          white-space: nowrap;
        }

        .cta-arrow {
          color: #94a3b8;
          transition: transform 0.15s ease;
        }
        .cta-card:hover .cta-arrow {
          transform: translate(2px, -2px);
          color: #ffffff;
        }

        .cta-subtitle {
          font-size: 0.78rem;
          color: #94a3b8;
          line-height: 1.3;
        }

        /* 4. Simplified Email Capture Form */
        .simplified-email-box {
          background: #090d14;
          border: 1px solid #1e293b;
          border-radius: var(--radius-md);
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .email-box-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.35rem;
        }

        .text-blue { color: #60a5fa; }
        .text-green { color: #10b981; }

        .email-box-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
        }

        .email-box-subtitle {
          font-size: 0.82rem;
          color: #94a3b8;
          margin: 0 0 1.15rem 0;
        }

        .simplified-form {
          width: 100%;
        }

        .form-inline-row {
          display: flex;
          gap: 0.75rem;
          width: 100%;
          align-items: center;
        }

        .input-field-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-email-wrap {
          flex: 1.35;
          min-width: 0;
        }

        .input-role-wrap {
          flex: 1.15;
          min-width: 0;
        }

        .single-work-input {
          width: 100%;
          background: #05080e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-sm);
          padding: 0.85rem 1rem;
          color: #ffffff;
          font-family: var(--font-body);
          font-size: 0.9rem;
          outline: none;
          min-height: 48px;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .single-work-input::placeholder {
          color: #64748b;
        }

        .single-work-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        .single-role-select {
          width: 100%;
          background: #05080e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-sm);
          padding: 0.85rem 2.2rem 0.85rem 0.95rem;
          color: #64748b;
          font-family: var(--font-body);
          font-size: 0.88rem;
          outline: none;
          min-height: 48px;
          box-sizing: border-box;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
        }

        .single-role-select.has-value {
          color: #f1f5f9;
        }

        .single-role-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        .single-role-select option {
          background: #090d14;
          color: #ffffff;
          padding: 0.5rem;
        }

        .role-select-chevron {
          position: absolute;
          right: 0.85rem;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #64748b;
        }

        .btn-submit-single {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          background: #2563eb;
          color: #ffffff;
          font-weight: 600;
          font-size: 0.9rem;
          border: none;
          padding: 0.85rem 1.35rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          min-height: 48px;
          flex-shrink: 0;
        }

        .btn-submit-single:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        .btn-submit-single:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        /* Success View in Form */
        .form-success-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .success-banner {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-size: 0.9rem;
          color: #e2e8f0;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          padding: 0.75rem 1rem;
          border-radius: var(--radius-sm);
        }

        .success-text-col {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .success-main-text {
          font-size: 0.9rem;
          color: #f1f5f9;
        }

        .success-role-tag {
          display: inline-block;
          font-size: 0.72rem;
          color: #34d399;
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          padding: 0.1rem 0.5rem;
          border-radius: 4px;
          width: fit-content;
        }

        .instant-action-row {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .btn-instant-small {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.6rem 0.9rem;
          border-radius: var(--radius-sm);
          font-size: 0.8rem;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          border: none;
          transition: all 0.15s ease;
        }

        .btn-mail-instant {
          background: #2563eb;
          color: #ffffff;
        }
        .btn-mail-instant:hover { background: #1d4ed8; }

        .btn-dl-instant {
          background: #1e293b;
          color: #ffffff;
        }
        .btn-dl-instant:hover { background: #334155; }

        .btn-reenter-email {
          background: none;
          border: none;
          color: #64748b;
          font-size: 0.75rem;
          cursor: pointer;
          text-align: left;
          text-decoration: underline;
          padding: 0;
        }
        .btn-reenter-email:hover { color: #ffffff; }

        /* 5. Quick Reference Section */
        .quick-reference-section {
          margin-bottom: 1.75rem;
        }

        .reference-heading {
          font-size: 0.68rem;
          color: #64748b;
          letter-spacing: 0.08em;
          display: block;
          margin-bottom: 0.75rem;
        }

        .reference-cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 0.85rem;
        }

        .reference-card {
          background: #090d14;
          border: 1px solid #1e293b;
          border-radius: var(--radius-sm);
          padding: 0.9rem;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .ref-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.35rem;
        }

        .ref-title {
          font-size: 0.82rem;
          font-weight: 600;
          color: #ffffff;
        }

        .ref-metric {
          font-size: 0.65rem;
          color: #34d399;
          background: rgba(16, 185, 129, 0.1);
          padding: 0.1rem 0.35rem;
          border-radius: 3px;
          border: 1px solid rgba(16, 185, 129, 0.2);
          white-space: nowrap;
        }

        .ref-desc {
          font-size: 0.78rem;
          color: #94a3b8;
          line-height: 1.4;
          margin: 0;
        }

        /* 6. Subtle Footnote */
        .footnote-deep-explore {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 1.25rem;
          border-top: 1px solid #1e293b;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .footnote-text {
          font-size: 0.78rem;
          color: #64748b;
        }

        .footnote-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .footnote-link-btn {
          background: transparent;
          border: 1px solid #1e293b;
          color: #94a3b8;
          font-size: 0.72rem;
          padding: 0.35rem 0.65rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .footnote-link-btn:hover {
          color: #ffffff;
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }

        .footnote-explore-btn {
          background: transparent;
          border: 1px solid #1e293b;
          color: #94a3b8;
          font-size: 0.72rem;
          padding: 0.35rem 0.75rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.15s ease;
        }

        .footnote-explore-btn:hover {
          color: #ffffff;
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }

        /* Mobile Breakpoints */
        @media (max-width: 850px) {
          .primary-cta-grid {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .reference-cards-grid {
            grid-template-columns: 1fr;
            gap: 0.65rem;
          }

          .engagement-card {
            padding: 1.35rem;
          }

          .engagement-headline {
            font-size: 1.45rem;
          }

          .engagement-subheadline {
            font-size: 0.95rem;
          }

          .form-inline-row {
            flex-direction: column;
            gap: 0.75rem;
          }

          .input-email-wrap,
          .input-role-wrap,
          .btn-submit-single {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};
